import type { WilsonMood } from "./ui/WilsonPresence"

export const LANDED_MS = 6_000

export interface IslandRun {
    threadId: string | null
    question: string
    step: string | null
}

export interface IslandLanding {
    threadId: string | null
    line: string
    subline: string | null
}

export interface IslandWilsonState {
    runs: Record<string, IslandRun>
    landing: IslandLanding | null
}

export type IslandWilsonAction =
    | { type: "runReported"; key: string; run: IslandRun }
    | { type: "runEnded"; key: string }
    | { type: "runLanded"; key: string; landing: IslandLanding }
    | { type: "landingCleared" }

export const IDLE_ISLAND_STATE: IslandWilsonState = { runs: {}, landing: null }

function withoutRun(
    runs: Record<string, IslandRun>,
    key: string
): Record<string, IslandRun> {
    if (!(key in runs)) return runs
    const rest = { ...runs }
    delete rest[key]
    return rest
}

export function islandWilsonReducer(
    state: IslandWilsonState,
    action: IslandWilsonAction
): IslandWilsonState {
    switch (action.type) {
        case "runReported":
            return {
                ...state,
                runs: { ...state.runs, [action.key]: action.run },
            }
        case "runEnded": {
            const runs = withoutRun(state.runs, action.key)
            return runs === state.runs ? state : { ...state, runs }
        }
        case "runLanded":
            return {
                runs: withoutRun(state.runs, action.key),
                landing: action.landing,
            }
        case "landingCleared":
            return state.landing ? { ...state, landing: null } : state
    }
}

export type IslandTarget =
    { kind: "thread"; threadId: string } | { kind: "none" }

export type IslandWilsonView =
    | { phase: "idle" }
    | { phase: "working"; line: string; target: IslandTarget }
    | {
          phase: "landed"
          line: string
          subline: string | null
          target: IslandTarget
      }

export interface IslandWilsonCopy {
    workingOn: (question: string) => string
    working: string
}

function threadTarget(threadId: string | null): IslandTarget {
    return threadId ? { kind: "thread", threadId } : { kind: "none" }
}

const SHORT_QUESTION_MAX_CHARS = 48

export function shortQuestion(question: string): string {
    const firstLine = question.trim().split(/\n/)[0]?.replace(/\s+/g, " ") ?? ""
    if (firstLine.length <= SHORT_QUESTION_MAX_CHARS) return firstLine
    const cut = firstLine.slice(0, SHORT_QUESTION_MAX_CHARS)
    const lastSpace = cut.lastIndexOf(" ")
    return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[\s,.;:!?]+$/, "")}…`
}

export function workingLine({
    run,
    copy,
}: {
    run: IslandRun
    copy: IslandWilsonCopy
}): string {
    if (run.step) return run.step
    const question = shortQuestion(run.question)
    return question ? copy.workingOn(question) : copy.working
}

export function deriveIslandWilsonView({
    state,
    copy,
}: {
    state: IslandWilsonState
    copy: IslandWilsonCopy
}): IslandWilsonView {
    const run = Object.values(state.runs).at(-1)
    if (run) {
        return {
            phase: "working",
            line: workingLine({ run, copy }),
            target: threadTarget(run.threadId),
        }
    }
    if (state.landing) {
        return {
            phase: "landed",
            line: state.landing.line,
            subline: state.landing.subline,
            target: threadTarget(state.landing.threadId),
        }
    }
    return { phase: "idle" }
}

export function islandWilsonMotion({
    phase,
    isReducedMotion,
}: {
    phase: IslandWilsonView["phase"]
    isReducedMotion: boolean
}): { mood: WilsonMood; isShimmering: boolean } {
    if (isReducedMotion) return { mood: "still", isShimmering: false }
    if (phase === "working") return { mood: "thinking", isShimmering: true }
    return { mood: "idle", isShimmering: false }
}
